import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Renders 1-4 post images as a responsive grid.
 * Click any image to open a full-screen lightbox with prev/next navigation.
 *
 * Backwards-compatible: if `images` is empty/undefined but `imageUrl` is set,
 * renders a single-image card.
 */
export default function PostImageGrid({ images, imageUrl, alt = 'Post image' }) {
  const list = (images && images.length > 0) ? images : (imageUrl ? [imageUrl] : []);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  if (list.length === 0) return null;

  const open = (idx) => (e) => {
    e.stopPropagation();
    setLightboxIdx(idx);
  };
  const close = () => setLightboxIdx(null);
  const prev = (e) => { e.stopPropagation(); setLightboxIdx((i) => (i + list.length - 1) % list.length); };
  const next = (e) => { e.stopPropagation(); setLightboxIdx((i) => (i + 1) % list.length); };

  // Layout patterns: 1 = full, 2 = side-by-side, 3 = 1 wide + 2 stacked, 4 = 2x2
  const single = list.length === 1;
  const triple = list.length === 3;
  const gridCls = single ? 'grid-cols-1' : triple ? 'grid-cols-2' : 'grid-cols-2';

  return (
    <>
      <div
        className={`grid gap-1 rounded-xl overflow-hidden ${gridCls}`}
        data-testid="post-image-grid"
      >
        {list.map((url, idx) => {
          // For 3-image: first image spans full height of left column
          const tripleFirst = triple && idx === 0;
          return (
            <button
              type="button"
              key={url + idx}
              onClick={open(idx)}
              className={`relative overflow-hidden bg-muted ${tripleFirst ? 'row-span-2' : ''}`}
              data-testid={`post-image-${idx}`}
            >
              <img
                src={url}
                alt={`${alt} ${idx + 1}`}
                className={`w-full h-full object-cover ${single ? 'max-h-[480px]' : 'h-48'} hover:opacity-95 transition-opacity`}
                loading="lazy"
              />
            </button>
          );
        })}
      </div>

      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={close}
          data-testid="image-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); close(); }}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          {list.length > 1 && (
            <>
              <button
                type="button"
                onClick={prev}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white bg-black/30 rounded-full"
                aria-label="Previous"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-7 h-7" />
              </button>
              <button
                type="button"
                onClick={next}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white bg-black/30 rounded-full"
                aria-label="Next"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-7 h-7" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
                {lightboxIdx + 1} / {list.length}
              </div>
            </>
          )}
          <img
            src={list[lightboxIdx]}
            alt={`${alt} ${lightboxIdx + 1}`}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
